import { memo, useContext, useEffect, useMemo } from 'react';
import { CriteriaRating, RubricField } from '@scale/shared/types/params/textcollection';
import { Card } from 'react-scale/components/scalers/tasks/ThreadedInteraction/Card';
import { RubricFieldAnnotation } from '@scale/shared/types/response/textcollection';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import produce from 'immer';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  isRubricAnnotationMissingRatings,
  filterOutStaleCriteriaRatings,
  reorderCriteriaRatings,
} from '@scale/shared/textcollection/helpers';
import { Callout, Icon, TextArea } from '@scale/scaleui-radix';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { Typography } from '@scale/scaleui';
import { RubricDimensionsDropdowns } from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/fields/RubricDimensionsDropdowns';
import AttachmentGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/AttachmentGroup';
import useMultimodalImageService from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import useMountTime from 'react-scale/hooks/useMountTime';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

interface RubricFieldProps {
  field: RubricField;
  setAnnotation: (annotation: RubricFieldAnnotation) => void;
  annotation: RubricFieldAnnotation;
  stepIndex?: number;
  candidateId?: string;
  disabled?: boolean;
}

const RubricScoresFieldSection = ({
  setAnnotation,
  annotation,
  field,
  stepIndex,
  candidateId,
  disabled,
}: RubricFieldProps) => {
  const responses = useChatStoreExtract(state => state.responses);
  const criterias = useMemo(() => {
    try {
      // NOTE: Load annotations from props
      if (stepIndex != null) {
        // NOTE: Load annotations from chat store if they are not loaded from props
        const currTCPerResponse = responses[stepIndex];
        const annotations = currTCPerResponse?.context.annotations;
        if (annotations && candidateId) {
          const rubricAnnotation: RubricFieldAnnotation = annotations[candidateId].annotations[
            field.field_id
          ] as RubricFieldAnnotation;
          return rubricAnnotation.response[0].criterias;
        }
      }
      return [];
    } catch (e) {
      // The current logger doesn't work. Surrounded with a try catch since the annotations are nested and brittle.
      // eslint-disable-next-line no-console
      console.log(e);
      return [];
    }
  }, [candidateId, field.field_id, responses, stepIndex]);

  const scores = useMemo(() => annotation?.response[0]?.scores ?? [], [annotation?.response]);
  const criteriaRatings = useMemo(
    () => filterOutStaleCriteriaRatings(criterias, annotation?.response[0]?.criteriaRatings ?? []),
    [annotation?.response, criterias],
  );

  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const taskContext = useContext(ChatTaskContext);
  const startTime = useMountTime();
  const multimodalImageServiceHook = useMultimodalImageService({
    projectId: taskContext.projectId,
    assignmentId: taskContext.assignmentId,
    configVariables: config.templateVariables ?? {},
    reviewLevel,
    startTime,
  });

  useEffect(() => {
    // NOTE: Set criterias and scores if they are not initialized
    if (annotation?.response[0]) {
      const newAnnotation = produce(annotation, draft => {
        draft.response[0].criterias = criterias;
        draft.response[0].scores = scores;
        draft.response[0].criteriaRatings = criteriaRatings;
      });
      setAnnotation(newAnnotation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterias]);

  const updateProperty = ({
    value,
    criteriaIndex,
  }: {
    value: CriteriaRating;
    criteriaIndex: number;
  }) => {
    const criteriaRating = criteriaRatings.find(
      rating => rating.criteria === criterias[criteriaIndex].criteria,
    );
    let newCriteriaRatings = !criteriaRating
      ? [
          ...criteriaRatings,
          {
            criteria: criterias[criteriaIndex].criteria,
            rating: value.rating,
            justification: value.justification,
            ratings: value.ratings,
            score: value.score,
          },
        ]
      : criteriaRatings.map(rating =>
          rating.criteria === criterias[criteriaIndex].criteria
            ? {
                ...rating,
                rating: value.rating,
                justification: value.justification,
                ratings: value.ratings,
                score: value.score,
              }
            : rating,
        );

    newCriteriaRatings = filterOutStaleCriteriaRatings(criterias, newCriteriaRatings);
    newCriteriaRatings = reorderCriteriaRatings(criterias, newCriteriaRatings);

    const newAnnotation = {
      ...annotation,
      response: [
        {
          criterias,
          criteriaRatings: newCriteriaRatings,
          scores: [],
        },
      ],
    };

    setAnnotation(newAnnotation);
  };

  const isMissingRatings = useMemo(
    () =>
      isRubricAnnotationMissingRatings(
        criterias,
        criteriaRatings,
        scores,
        field.requireJustification,
        field.dimensions,
      ),
    [criteriaRatings, criterias, field.dimensions, field.requireJustification, scores],
  );

  if (field.ratings) {
    return (
      <>
        <Card subtitle={'Rubric'}>
          {isMissingRatings && (
            <Callout.Root size="1" color="warning" className="mb-4">
              <Callout.Icon>
                <Icon icon={faCircleInfo} label="warning" />
              </Callout.Icon>
              <Callout.Text>
                Some rubric items are not rated. Please select an evaluation for each rubric item
              </Callout.Text>
            </Callout.Root>
          )}
          <div className="space-y-4">
            {criterias?.map((criteria, i) => {
              const selectedRating = criteriaRatings.find(
                rating => rating.criteria === criterias[i].criteria,
              );
              const updateRating = ({
                rating,
                dimension,
              }: {
                rating: string;
                dimension?: string;
              }) => {
                const updatedRating = dimension == null ? rating : selectedRating?.rating ?? '';
                const updatedRatings =
                  dimension == null
                    ? selectedRating?.ratings ?? {}
                    : {
                        ...selectedRating?.ratings,
                        [dimension]: { rating },
                      };
                updateProperty({
                  value: {
                    criteria: criteria.criteria,
                    rating: updatedRating,
                    justification: selectedRating?.justification,
                    score: field.ratings.find(r => r.value === updatedRating)?.score,
                    ratings: updatedRatings,
                  },
                  criteriaIndex: i,
                });
              };
              return (
                <Card
                  key={i}
                  className="rounded-lg p-4"
                  customHeader={
                    <div className="flex items-start py-2">
                      <div className="flex-shrink-0 mt-[1.5px] w-6 text-neutral-500">{i + 1}.</div>
                      <ChatMarkdown
                        markdown={criteria.criteria}
                        className="flex-grow border-none p-0"
                      />
                    </div>
                  }
                >
                  <div className="flex flex-col gap-2">
                    {field.enableAttachments &&
                      criteria.attachments &&
                      criteria.attachments.length > 0 && (
                        <AttachmentGroup
                          attachments={criteria.attachments ?? []}
                          setAttachments={() => {}}
                          multimodalImageServiceHook={multimodalImageServiceHook}
                          enableAttachments={field.enableAttachments}
                          readOnly
                        />
                      )}
                    <RubricDimensionsDropdowns
                      ratingsWithoutDimensions={field.ratings}
                      ratingWithoutDimensionForCriteria={selectedRating?.rating}
                      allDimensions={field.dimensions ?? []}
                      ratingsForCriteria={selectedRating?.ratings ?? {}}
                      updateRating={updateRating}
                      disabled={disabled}
                    />
                    {field.requireJustification && (
                      <div className="flex flex-col gap-2">
                        <Typography.Paragraph3>Justification *</Typography.Paragraph3>
                        <TextArea
                          className="w-full"
                          value={selectedRating?.justification || ''}
                          onChange={e =>
                            updateProperty({
                              value: {
                                criteria: criteria.criteria,
                                rating: selectedRating?.rating ?? '',
                                justification: e.target.value,
                                score: field.ratings.find(r => r.value === selectedRating?.rating)
                                  ?.score,
                                ratings: selectedRating?.ratings ?? {},
                              },
                              criteriaIndex: i,
                            })
                          }
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </Card>
        <br />
      </>
    );
  }

  return (
    <>
      <Card subtitle={'Rubric'}>
        {criterias?.map((criteria, i) => (
          <BooleanInputBuilder
            key={criteria.criteria}
            name={criteria.criteria}
            existingValue={!!scores[i]}
            updateProperty={() => {
              const newAnnotation = produce(annotation, draft => {
                // Ensure draft.response is initialized
                if (!draft.response) {
                  draft.response = [];
                }

                // Ensure draft.response[0] is initialized
                if (!draft.response[0]) {
                  draft.response[0] = {
                    scores: [],
                    criterias: [],
                    criteriaRatings: [],
                  };
                }

                // Ensure draft.response[0].scores is initialized
                if (!draft.response[0].scores) {
                  draft.response[0].scores = [];
                }
                draft.response[0].scores[i] = !scores[i];

                // Ensure draft.response[0].criterias is initialized
                if (!draft.response[0].criterias) {
                  draft.response[0].criterias = [];
                }
                draft.response[0].criterias = criterias;
              });
              setAnnotation(newAnnotation);
            }}
            disabled={disabled}
          />
        ))}
      </Card>
      <br />
    </>
  );
};

export default memo(RubricScoresFieldSection);
